// ortbLint.js — lean structural linter for OpenRTB 2.5/2.6 bid requests and
// bid responses. Paste-and-check tool: caller parses JSON, this just walks
// the object and flags structural issues. Never throws.
//
// ortbLint(obj) -> { ok, kind: 'request'|'response'|'unknown', findings: [{ path, level, code, message }] }

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function detectKind(obj) {
  if (Array.isArray(obj.imp)) return 'request';
  if (Array.isArray(obj.seatbid)) return 'response';
  return 'unknown';
}

function push(findings, path, level, code, message) {
  findings.push({ path, level, code, message });
}

/* ── request checks ────────────────────────────────────────────────── */

function lintImp(imp, idx, findings) {
  const path = `imp[${idx}]`;
  if (!imp || typeof imp !== 'object') {
    push(findings, path, 'error', 'imp_not_object', 'imp entry is not an object');
    return;
  }
  if (imp.id === undefined || imp.id === null || imp.id === '') {
    push(findings, `${path}.id`, 'error', 'imp_missing_id', 'imp missing id');
  }

  const hasBanner = isPlainObject(imp.banner);
  const hasVideo = isPlainObject(imp.video);
  const hasNative = isPlainObject(imp.native);
  const hasAudio = isPlainObject(imp.audio);
  if (!hasBanner && !hasVideo && !hasNative && !hasAudio) {
    push(findings, path, 'error', 'imp_no_format', 'imp has neither banner, video, native, nor audio');
  }

  if (hasBanner) {
    const b = imp.banner;
    const hasWH = typeof b.w === 'number' && typeof b.h === 'number';
    const hasFormat = Array.isArray(b.format) && b.format.length > 0;
    if (!hasWH && !hasFormat) {
      push(findings, `${path}.banner`, 'warn', 'banner_size_missing', 'banner has no w/h and no format array');
    }
  }

  if (hasVideo) {
    const v = imp.video;
    const vPath = `${path}.video`;
    if (!Array.isArray(v.mimes) || v.mimes.length === 0) {
      push(findings, `${vPath}.mimes`, 'error', 'video_missing_mimes', 'video object missing mimes');
    }
    if (!Array.isArray(v.protocols) || v.protocols.length === 0) {
      push(findings, `${vPath}.protocols`, 'warn', 'video_missing_protocols', 'video object missing protocols');
    }
    const hasPlcmt = v.plcmt !== undefined && v.plcmt !== null;
    const hasPlacement = v.placement !== undefined && v.placement !== null;
    if (!hasPlcmt && !hasPlacement) {
      push(findings, vPath, 'warn', 'video_missing_placement', 'video object has neither plcmt (2.6) nor placement — placement type unknown');
    }
    if (typeof v.minduration === 'number' && typeof v.maxduration === 'number' && v.minduration > v.maxduration) {
      push(findings, vPath, 'error', 'video_minduration_gt_maxduration', 'video minduration is greater than maxduration');
    }
  }
}

function lintDevice(device, findings) {
  if (!isPlainObject(device)) return;
  const path = 'device';
  if (device.ua === undefined || device.ua === null || device.ua === '') {
    push(findings, `${path}.ua`, 'info', 'device_missing_ua', 'device missing ua');
  }
  const hasIfa = typeof device.ifa === 'string' && device.ifa.length > 0;
  const lmt1 = device.lmt === 1 || device.lmt === '1';
  if (hasIfa && lmt1) {
    push(findings, path, 'warn', 'ifa_with_lmt', 'ID sent despite limit-tracking (ifa present with lmt=1)');
  }
  if (device.devicetype === undefined || device.devicetype === null) {
    push(findings, `${path}.devicetype`, 'info', 'device_missing_devicetype', 'device missing devicetype');
  }
}

function lintRegsAndUser(obj, findings) {
  const gdpr = obj.regs && obj.regs.ext && obj.regs.ext.gdpr;
  const gdprPresent = gdpr !== undefined && gdpr !== null;
  if (gdprPresent) {
    const consent = obj.user && obj.user.ext && obj.user.ext.consent;
    const consentPresent = consent !== undefined && consent !== null && consent !== '';
    if (!consentPresent) {
      push(findings, 'user.ext.consent', 'warn', 'gdpr_without_consent', 'regs.ext.gdpr present but user.ext.consent absent');
    }
  }

  const eids = obj.user && obj.user.ext && obj.user.ext.eids;
  if (Array.isArray(eids)) {
    eids.forEach((eid, i) => {
      const path = `user.ext.eids[${i}]`;
      if (!eid || typeof eid !== 'object') {
        push(findings, path, 'error', 'eid_invalid', 'eid entry is not an object');
        return;
      }
      if (!eid.source) {
        push(findings, `${path}.source`, 'error', 'eid_missing_source', 'eid missing source');
      }
      if (!Array.isArray(eid.uids) || eid.uids.length === 0) {
        push(findings, `${path}.uids`, 'error', 'eid_missing_uids', 'eid missing uids[]');
      } else {
        eid.uids.forEach((uid, j) => {
          if (!uid || typeof uid !== 'object' || uid.id === undefined || uid.id === null || uid.id === '') {
            push(findings, `${path}.uids[${j}].id`, 'error', 'eid_uid_missing_id', 'eid uid entry missing id');
          }
        });
      }
    });
  }
}

function lintRequest(obj, findings) {
  if (obj.id === undefined || obj.id === null || obj.id === '') {
    push(findings, 'id', 'error', 'request_missing_id', 'request missing id');
  }

  if (!Array.isArray(obj.imp) || obj.imp.length === 0) {
    push(findings, 'imp', 'error', 'imp_empty', 'imp array missing or empty');
  } else {
    obj.imp.forEach((imp, idx) => lintImp(imp, idx, findings));
  }

  const hasApp = isPlainObject(obj.app);
  const hasSite = isPlainObject(obj.site);
  if (hasApp && hasSite) {
    push(findings, '', 'error', 'app_and_site_both_present', 'both app and site are present — request should declare exactly one');
  } else if (!hasApp && !hasSite) {
    push(findings, '', 'warn', 'app_and_site_both_absent', 'neither app nor site is present');
  }

  lintDevice(obj.device, findings);
  lintRegsAndUser(obj, findings);

  if (obj.tmax === undefined || obj.tmax === null) {
    push(findings, 'tmax', 'info', 'tmax_missing', 'tmax missing — no auction timeout declared');
  }

  if (obj.cur !== undefined && !Array.isArray(obj.cur)) {
    push(findings, 'cur', 'error', 'cur_not_array', 'cur is present but is not an array');
  }
}

/* ── response checks ───────────────────────────────────────────────── */

function lintBid(bid, path, findings) {
  if (!bid || typeof bid !== 'object') {
    push(findings, path, 'error', 'bid_not_object', 'bid entry is not an object');
    return;
  }
  if (typeof bid.price !== 'number') {
    push(findings, `${path}.price`, 'error', 'bid_missing_price', 'bid missing price');
  }
  const hasAdm = bid.adm !== undefined && bid.adm !== null && bid.adm !== '';
  const hasNurl = bid.nurl !== undefined && bid.nurl !== null && bid.nurl !== '';
  if (!hasAdm && !hasNurl) {
    push(findings, path, 'warn', 'bid_missing_adm_and_nurl', 'bid has neither adm nor nurl');
  }
  if (bid.crid === undefined || bid.crid === null || bid.crid === '') {
    push(findings, `${path}.crid`, 'info', 'bid_missing_crid', 'bid missing crid');
  }
  if (!Array.isArray(bid.adomain) || bid.adomain.length === 0) {
    push(findings, `${path}.adomain`, 'warn', 'bid_missing_adomain', 'no advertiser domain — blocklist checks impossible');
  }
}

function lintResponse(obj, findings) {
  if (obj.id === undefined || obj.id === null || obj.id === '') {
    push(findings, 'id', 'warn', 'response_missing_id', 'response missing id');
  }

  obj.seatbid.forEach((seat, i) => {
    const seatPath = `seatbid[${i}]`;
    if (!seat || typeof seat !== 'object') {
      push(findings, seatPath, 'error', 'seat_not_object', 'seatbid entry is not an object');
      return;
    }
    if (!Array.isArray(seat.bid) || seat.bid.length === 0) {
      push(findings, `${seatPath}.bid`, 'warn', 'seat_missing_bids', 'seatbid entry has no bids');
      return;
    }
    seat.bid.forEach((bid, j) => lintBid(bid, `${seatPath}.bid[${j}]`, findings));
  });
}

/* ── entry point ────────────────────────────────────────────────────── */

export function ortbLint(obj) {
  const findings = [];

  if (!isPlainObject(obj)) {
    push(findings, '', 'error', 'not_an_object', 'input is not a JSON object');
    return { ok: false, kind: 'unknown', findings };
  }

  try {
    const kind = detectKind(obj);

    if (kind === 'request') {
      lintRequest(obj, findings);
    } else if (kind === 'response') {
      lintResponse(obj, findings);
    } else {
      push(findings, '', 'warn', 'unknown_kind', 'could not detect request (imp[]) or response (seatbid[]) shape');
    }

    const ok = !findings.some((f) => f.level === 'error');
    return { ok, kind, findings };
  } catch (e) {
    push(findings, '', 'error', 'unexpected_error', 'unexpected error linting object: ' + (e && e.message ? e.message : String(e)));
    return { ok: false, kind: 'unknown', findings };
  }
}

export default ortbLint;
